import React from 'react'
import PropTypes from 'prop-types'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip
} from 'recharts'
import { Box, Typography, Card, CardContent, Grid, Paper } from '@mui/material'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import AssessmentIcon from '@mui/icons-material/Assessment'
import InfoIcon from '@mui/icons-material/Info'
import moment from 'moment-timezone'
import { chartConfig, dateFormats } from '../../../consts'
import styles from './EnhancedMetricsCharts.module.scss'

const GRADIENT_COLORS = chartConfig.gradientColors

const CustomTooltip = ({ active = false, payload = [], label = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div className={styles.customTooltip}>
        <p className={styles.customTooltipHeader}>{
          (() => {
            try {
              if (typeof label === 'string' && (label.includes('-') || label.includes('T'))) {
                const dateOnly = label.includes('T') ? label.split('T')[0] : label
                return moment.tz(dateOnly, 'YYYY-MM-DD', dateFormats.timezone).format('MM/DD/YY')
              }
              return moment(label).tz(dateFormats.timezone).format('MM/DD/YY')
            } catch (e) {
            }
            return label
          })()
        }
        </p>
        {payload.map((entry, index) => (
          <p
            key={index}
            className={styles.customTooltipItem}
          >
            <span
              className={styles.customTooltipDot}
              style={{ background: entry.color }}
            />
            <span className={styles.customTooltipValue}>
              {`${entry.name}: `}
              <strong>{entry.value}</strong>
            </span>
          </p>
        ))}
      </div>
    )
  }
  return null
}

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string
  })),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

const formatXAxisDate = (value) => {
  try {
    if (typeof value === 'string' && (value.includes('-') || value.includes('T'))) {
      const dateOnly = value.includes('T') ? value.split('T')[0] : value
      return moment.tz(dateOnly, 'YYYY-MM-DD', dateFormats.timezone).format('MM/DD/YY')
    }
    return moment(value).tz(dateFormats.timezone).format('MM/DD/YY')
  } catch (e) {
    return value
  }
}

const MetricsLineChart = ({
  title,
  chartData,
  dataKey,
  dataName,
  gradientId,
  gradientColors = null,
  loading,
  classes,
  paperClassName,
  formatTooltip = null,
  yAxisFormatter
}) => {
  const hasChartData = chartData && Array.isArray(chartData) && chartData.length > 0
  const showWarning = !hasChartData
  const colors = gradientColors || GRADIENT_COLORS[gradientId] || ['#2B5876', '#4E4376']

  const tooltipContent = formatTooltip
    ? (props) => {
      const { active, payload, label } = props
      if (active && payload && payload.length) {
        const formattedPayload = formatTooltip(payload)
        return CustomTooltip({ active, payload: formattedPayload, label })
      }
      return null
    }
    : <CustomTooltip />

  const fillGradientId = `${gradientId}Fill`

  return (
    <div className={`${paperClassName} ${styles.chartPaper} card-interactive`}>
      <Box className={styles.chartPaperHeader}>
        <AssessmentIcon className={styles.chartPaperIcon} />
        <Typography variant='h6' component='h2' className={styles.sectionTitle}>
          {title}
        </Typography>
      </Box>

      <Grid item xs={12}>
        <Card className={`${classes.chartCard} chart-card`}>
          <CardContent className={classes.chartContent}>
            {loading
              ? (
                <Skeleton
                  height={380}
                  className={styles.skeletonChart}
                  aria-busy='true'
                  aria-label='Loading chart data'
                />
                )
              : showWarning
                ? (
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    justifyContent='center'
                    height={380}
                    textAlign='center'
                  >
                    <Paper className={classes.warningMessage}>
                      <Box className={classes.warningHeader}>
                        <InfoIcon />
                        <Typography variant='h6' component='p'>
                          No Chart Data Available
                        </Typography>
                      </Box>
                      <Typography variant='body1' gutterBottom>
                        No chart data is available for the currently selected date range.
                      </Typography>
                      <Typography variant='body2'>
                        Try selecting a broader or more recent date range to view available data.
                      </Typography>
                    </Paper>
                  </Box>
                  )
                : (
                  <>
                  <div role='img' aria-label={`${title} chart`}>
                  <ResponsiveContainer width='100%' height={380}>
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                    >
                      <defs>
                        <linearGradient id={gradientId} x1='0' y1='0' x2='1' y2='0'>
                          <stop offset='0%' stopColor={colors[0]} stopOpacity={1} />
                          <stop offset='100%' stopColor={colors[1]} stopOpacity={1} />
                        </linearGradient>
                        <linearGradient id={fillGradientId} x1='0' y1='0' x2='0' y2='1'>
                          <stop offset='0%' stopColor={colors[0]} stopOpacity={0.25} />
                          <stop offset='60%' stopColor={colors[1]} stopOpacity={0.08} />
                          <stop offset='100%' stopColor={colors[1]} stopOpacity={0.01} />
                        </linearGradient>
                        <filter id={`${gradientId}Glow`} x='-20%' y='-20%' width='140%' height='140%'>
                          <feGaussianBlur stdDeviation='3' result='blur' />
                          <feMerge>
                            <feMergeNode in='blur' />
                            <feMergeNode in='SourceGraphic' />
                          </feMerge>
                        </filter>
                      </defs>
                      <CartesianGrid
                        strokeDasharray='6 6'
                        stroke='var(--color-border)'
                        vertical={false}
                        strokeOpacity={0.4}
                      />
                      <XAxis
                        dataKey='date'
                        tick={{
                          fill: '#94a3b8',
                          fontSize: 11,
                          fontWeight: 500
                        }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={formatXAxisDate}
                        dy={8}
                        tickMargin={8}
                      />
                      <YAxis
                        tick={{
                          fill: '#94a3b8',
                          fontSize: 11,
                          fontWeight: 500
                        }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={yAxisFormatter}
                        dx={-4}
                      />
                      <RechartsTooltip
                        content={tooltipContent}
                        cursor={{
                          stroke: colors[0],
                          strokeWidth: 1,
                          strokeDasharray: '4 4',
                          strokeOpacity: 0.4
                        }}
                      />
                      <Area
                        type='monotone'
                        dataKey={dataKey}
                        stroke={`url(#${gradientId})`}
                        fill={`url(#${fillGradientId})`}
                        fillOpacity={1}
                        strokeWidth={2.5}
                        name={dataName}
                        animationDuration={1200}
                        animationEasing='ease-out'
                        animationBegin={200}
                        activeDot={{
                          r: 6,
                          strokeWidth: 2,
                          stroke: '#fff',
                          fill: colors[0],
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))'
                        }}
                        dot={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                  </div>

                  </>
                  )}
          </CardContent>
        </Card>
      </Grid>
    </div>
  )
}

MetricsLineChart.propTypes = {
  title: PropTypes.string.isRequired,
  chartData: PropTypes.array.isRequired,
  dataKey: PropTypes.string.isRequired,
  dataName: PropTypes.string.isRequired,
  gradientId: PropTypes.string.isRequired,
  gradientColors: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  paperClassName: PropTypes.string.isRequired,
  formatTooltip: PropTypes.func,
  yAxisFormatter: PropTypes.func
}

export { CustomTooltip }
export default MetricsLineChart
